import type { components } from "@countdown/api-client";
import { type ReactElement, useState } from "react";

import { Notice, OutcomeNotice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LOAD_FAILED, type StyleGroup, applyStyle } from "@/lib/api";
import { ACTION_STATUS_LABELS, type BackofficeElement } from "@/lib/labels";

type ActionView = components["schemas"]["BackofficeActionView"];

/** The groups the server knows, in the order of their pages. */
const GROUPS: readonly (readonly [StyleGroup, string, string])[] = [
  [
    "calendar",
    "Kalender",
    "Anordnung, Raster, Gestaltung der Türchen, Türchenzahl, Fußzeile, Darstellung auf Websites",
  ],
  ["door_window", "Türchen-Fenster", "Darstellung und Standard-Aufbau"],
  // The consent modes travel with the form (D51): a target can gain a required consent.
  ["form", "Formular", "Felder, Reihenfolge, Texte und Einwilligungen des Teilnahmeformulars"],
  ["deadlines", "Fristen", "Einreichfrist, Freigabefrist und »Fristwarnungen an«"],
  [
    "sponsor_workflow",
    "Pflege durch Sponsoren",
    "Wer die Inhalte pflegt, was Sponsoren ändern dürfen, Aufbau durch Sponsoren, Benachrichtigungen",
  ],
];

/** The look on the tab »Gestaltung«, all preselected. */
export const LOOK_GROUPS: readonly StyleGroup[] = ["calendar", "door_window", "form"];
/** The settings on the tab »Einstellungen«; none preselected, they decide who approves (D55). */
export const SETTING_GROUPS: readonly StyleGroup[] = ["deadlines", "sponsor_workflow"];

/** The elements each group needs, all of them, as the server checks (`STYLE_GROUP_ELEMENTS`). */
const STYLE_GROUP_ELEMENTS: Readonly<Record<StyleGroup, readonly BackofficeElement[]>> = {
  calendar: ["calendar_layout", "calendar_style", "door_number_style", "action_embed"],
  door_window: ["door_window"],
  form: ["lead_fields"],
  deadlines: ["action_basics"],
  sponsor_workflow: ["sponsor_workflow", "action_notifications"],
};

/** The groups of `groups` the session may copy (D55, D57). */
export function copyableGroups(
  groups: readonly StyleGroup[],
  granted: readonly string[],
): StyleGroup[] {
  return groups.filter((group) =>
    STYLE_GROUP_ELEMENTS[group].every((element) => granted.includes(element)),
  );
}

/** A visitor sees a change to these at once. */
const RUNNING: ReadonlySet<ActionView["status"]> = new Set(["live", "paused"]);

/**
 * „Stil auf andere Aktionen übernehmen" (D37) and, with `groups={SETTING_GROUPS}`, the same
 * for deadlines and the sponsor workflow (D55): this action's saved values for others of the
 * cell in one click. The look is preselected, the settings are not. Ended actions are not offered, the server refuses them as it refuses
 * every other write to them. A target whose first door has opened keeps its form (D24); the
 * notice after the transfer names it. The server copies what is saved, so `unsaved` (the
 * calendar draft differs from it) earns a note in the dialog. There is no undo; each target's
 * audit row keeps what it had.
 */
export function StyleTransfer({
  source,
  actions,
  unsaved = false,
  groups: offered = LOOK_GROUPS,
}: {
  readonly source: Pick<ActionView, "action_id" | "title" | "customer_sponsor_id">;
  readonly actions: readonly ActionView[];
  readonly unsaved?: boolean;
  /** The groups this place offers, already narrowed to what the operator may write. */
  readonly groups?: readonly StyleGroup[];
}): ReactElement {
  const look = offered.every((group) => LOOK_GROUPS.includes(group));
  const shown = GROUPS.filter(([group]) => offered.includes(group));
  // Deadlines and the sponsor workflow go only between actions of the same calendar kind
  // (E5); the server leaves them out otherwise.
  const isCustomer = (action: Pick<ActionView, "customer_sponsor_id">): boolean =>
    (action.customer_sponsor_id ?? null) !== null;
  const targets = actions.filter(
    (action) =>
      action.action_id !== source.action_id &&
      action.status !== "finished" &&
      action.status !== "archived" &&
      (look || isCustomer(action) === isCustomer(source)),
  );
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [groups, setGroups] = useState<ReadonlySet<StyleGroup>>(
    () => new Set(look ? offered : []),
  );
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const toggle = <T,>(set: ReadonlySet<T>, value: T): ReadonlySet<T> => {
    const next = new Set(set);
    if (!next.delete(value)) {
      next.add(value);
    }
    return next;
  };

  const submit = (): void => {
    setBusy(true);
    setRefusal(null);
    applyStyle({
      source_action_id: source.action_id,
      target_action_ids: targets
        .filter((action) => chosen.has(action.action_id))
        .map((action) => action.action_id),
      groups: shown.map(([group]) => group).filter((group) => groups.has(group)),
    })
      .then((result) => {
        if (result.ok) {
          const count = result.actionIds.length;
          const kept = targets
            .filter((action) => result.formKept.includes(action.action_id))
            .map((action) => `»${action.title}«`);
          setDone(
            `${look ? "Stil" : "Einstellungen"} auf ${String(count)} ` +
              `${count === 1 ? "Aktion" : "Aktionen"} übernommen.` +
              (kept.length === 0
                ? ""
                : ` Das Formular bleibt bei ${kept.join(", ")}: Dort ist das erste Türchen ` +
                  "schon offen, und erteilte Einwilligungen nennen die bisherigen Felder."),
          );
          setChosen(new Set());
          setOpen(false);
        } else {
          setRefusal(result.message);
        }
      })
      .catch((error: unknown) => {
        console.error("[StyleTransfer] transfer failed", error);
        setRefusal(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="grid justify-items-start gap-3">
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          setRefusal(null);
          setDone(null);
        }}
      >
        <DialogTrigger asChild>
          <Button variant="outline" disabled={targets.length === 0}>
            {look ? "Stil" : "Einstellungen"} auf andere Aktionen übernehmen
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{look ? "Stil" : "Einstellungen"} übernehmen</DialogTitle>
            <DialogDescription>
              Die gewählten Aktionen übernehmen die Einstellungen von »{source.title}«.
              Marke, Titel, Link, Termine und die Websites, auf denen der Kalender
              eingebettet ist, bleiben, wie sie sind. Übernommen wird der gespeicherte Stand.
            </DialogDescription>
          </DialogHeader>
          {unsaved ? (
            <Notice tone="info">
              Der Kalender hat ungespeicherte Änderungen. Sie werden nicht übernommen, solange
              sie nicht gespeichert sind.
            </Notice>
          ) : null}
          <fieldset disabled={busy}>
            <legend className="text-sm font-bold">Aktionen</legend>
            <ul className="mt-2 grid gap-2">
              {targets.map((action) => {
                const id = `stil-ziel-${action.action_id}`;
                return (
                  <li key={action.action_id} className="flex items-start gap-2">
                    <input
                      className="mt-1 border border-input"
                      id={id}
                      type="checkbox"
                      checked={chosen.has(action.action_id)}
                      onChange={() => {
                        setChosen((current) => toggle(current, action.action_id));
                      }}
                    />
                    <label className="text-sm" htmlFor={id}>
                      {action.title}{" "}
                      <span className="text-muted-foreground">
                        ({ACTION_STATUS_LABELS[action.status]})
                      </span>
                      {RUNNING.has(action.status) ? (
                        <span className="block text-muted-foreground">
                          Läuft: Die Änderung ist sofort sichtbar.
                        </span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
          <fieldset disabled={busy}>
            <legend className="text-sm font-bold">Was übernommen wird</legend>
            <ul className="mt-2 grid gap-2">
              {shown.map(([group, label, hint]) => {
                const id = `stil-gruppe-${group}`;
                return (
                  <li key={group} className="flex items-start gap-2">
                    <input
                      className="mt-1 border border-input"
                      id={id}
                      type="checkbox"
                      checked={groups.has(group)}
                      onChange={() => {
                        setGroups((current) => toggle(current, group));
                      }}
                    />
                    <label className="text-sm" htmlFor={id}>
                      {label}
                      <span className="block text-muted-foreground">{hint}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
          {refusal !== null ? (
            <Notice tone="error" className="whitespace-pre-line">
              {refusal}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Abbrechen
            </Button>
            <Button disabled={busy || chosen.size === 0 || groups.size === 0} onClick={submit}>
              {busy ? "Wird übernommen …" : "Übernehmen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {done !== null ? <OutcomeNotice outcome={{ ok: true, message: done }} /> : null}
    </div>
  );
}
